import { lazy, Suspense, type ReactNode } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import useFeaturesReady from '@/shared/featureFlags/useFeaturesReady';
import { INVOICE_QR_MAINTENANCE_FLAG } from '../../constants/flag';
import type { QrMaintenanceSurface } from '../../hooks/useInvoiceV2Tracking';

const MAINTENANCE_SCREEN = {
  public: lazy(() =>
    import('./QrPublicMaintenance').then((module) => ({ default: module.QrPublicMaintenance })),
  ),
  owner: lazy(() =>
    import('../../v2/views/QrOwnerMaintenanceView').then((module) => ({
      default: module.QrOwnerMaintenanceView,
    })),
  ),
} satisfies Record<QrMaintenanceSurface, unknown>;

/**
 * La única compuerta de `invoice-qr-maintenance` sobre una ruta del QR. En
 * mantenimiento pinta la pantalla de su superficie y `children` ni se monta,
 * así que tampoco salen sus consultas. Espera a las banderas para que el
 * formulario no aparezca un instante y luego se tape; si no llegan, sirve lo
 * normal. Las pantallas se cargan aparte: la compuerta vive en el bundle de
 * entrada y ellas solo hacen falta en mantenimiento.
 */
export const QrMaintenanceGate = ({
  surface,
  children,
}: {
  surface: QrMaintenanceSurface;
  children: ReactNode;
}) => {
  const featuresReady = useFeaturesReady();
  const inMaintenance = useFeatureIsOn(INVOICE_QR_MAINTENANCE_FLAG);

  if (!featuresReady) return null;
  if (!inMaintenance) return children;
  const MaintenanceScreen = MAINTENANCE_SCREEN[surface];
  return (
    <Suspense fallback={null}>
      <MaintenanceScreen />
    </Suspense>
  );
};
